import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getUsersMemberships } from '@/services/api/users/users.js'

// ====---------------====
// 💾 Types & Interfaces
// ====---------------====

export type TMemberShipItem = {
	companyId: string
	contractId: string
	memberId: string
	memberSeqNumber: string
	isPolicyHolder: boolean
	isActive: boolean
	contractType: string
	origEffDate: string
	covTermDate: string
	lobDisplayName: string
	tier1: string
	ssoType: string | null
	displayName: string
}

interface IMembershipsResponse {
	userMemberships: Array<TMemberShipItem>
	validationErrors: []
}

export interface IPlansList {
	active: Array<TMemberShipItem>
	inactive: Array<TMemberShipItem>
}

export const useMembershipsStore = defineStore(
	'membershipsStore',
	() => {
		// ====---------------====
		// 🏪 Stores
		// ====---------------====

		// ====---------------====
		// 🗄️ State
		// ====---------------====
		const state = ref<IPlansList>({ active: [], inactive: [] })
		const loaded = ref(false)

		// ====---------------====
		// 🏃‍♂️ Getters
		// ====---------------====
		async function getStore() {
			// Initialize Count store
			if (!loaded.value) await loadStore()

			return state.value
		}

		function getFirstPlan(): TMemberShipItem {
			return state.value.active.length > 0 ? state.value.active[0] : state.value.inactive[0]
		}

		// ====---------------====
		// 🎬 Actions
		// ====---------------====
		function $reset() {
			state.value = { active: [], inactive: [] }
			loaded.value = false
		}

		function sortContacts(contracts: Array<TMemberShipItem>) {
			contracts.forEach((contract) => {
				contract.isActive ? state.value.active.push(contract) : state.value.inactive.push(contract)
			})
		}

		// ====---------------====
		// Loads the store with a promise
		// This keeps the store form loading multiple times
		// ====---------------====
		let loadStoreRequest: Promise<string> | null = null
		async function loadStore(username: string = sessionStorage.getItem('logged_in_user') ?? '') {
			if (loadStoreRequest) {
				return loadStoreRequest
			}
			loadStoreRequest = new Promise(async (resolve, reject) => {
				try {
					await getUsersMemberships(username).then((res: IMembershipsResponse) => {
						sortContacts(res.userMemberships)
						loaded.value = true
					})
					resolve('')
				} catch (err) {
					reject(err)
				} finally {
					loadStoreRequest = null
				}
			})
			return loadStoreRequest
		}

		// ====---------------====
		// 🚀 Exports
		// ====---------------====
		return {
			state,
			loaded,
			getStore,
			getFirstPlan,
			loadStore,
			$reset
		}
	},
	{
		persist: {
			storage: sessionStorage
		}
	}
)
